/**
 * AIZEn ad impression-tracking composable.
 *
 * Replaces the per-component `new IntersectionObserver(..., { threshold: 0.5 })`
 * blocks that every ad placement used to carry its own copy of.
 *
 * End of Article reported a CTR above 100% (3,068 clicks against 2,611
 * impressions) because clicks were counted correctly while impressions almost
 * never fired. Three separate defects produced that:
 *
 * 1. `threshold: 0.5` is unreachable for a creative taller than 2x the
 *    viewport.  `intersectionRatio` is visible-area / element-area, so an
 *    end-of-article unit measuring ~1,150px in a 433px viewport peaks at 0.38
 *    and the observer never fires at all.  A short banner clears 0.5 trivially,
 *    which is why only the tall placements undercounted.
 *
 * 2. The observer was attached from a `watch(ad, ...)` guarded by
 *    `if (!url || !container.value) return`.  That guard has no retry, so any
 *    tick where the element ref was not yet populated dropped impression
 *    tracking for the rest of the page view.
 *
 * 3. Every caller passes the URL as a getter — `() => ad.value?.impressionUrl`
 *    — but this composable resolved it with `unref`, which returns a function
 *    unchanged.  The guards saw a truthy value and `fetch` stringified the
 *    function body into a relative URL, so the pixel hit the content site
 *    instead of the ad server.  See `resolve` below.
 *
 * The fix keeps the IAB viewability intent (>=50% visible for >=1 continuous
 * second) but measures it in a way that stays reachable for large creatives,
 * and re-attaches whenever the element or URL becomes available.
 */

const DWELL_MS           = 1000
const VIEWABLE_FRACTION  = 0.5
const SAMPLE_MS          = 200

/**
 * IAB-style viewability test that also works for creatives larger than the
 * viewport.
 *
 * A unit counts as viewable when at least half of it is on screen, OR — for
 * creatives too tall to ever show half of themselves — when the visible slice
 * fills at least half the screen.
 *
 * @param {Element} el
 * @returns {boolean}
 */
export function isAdViewable(el) {
  if (!el || typeof window === 'undefined') return false

  const rect = el.getBoundingClientRect()
  const vh   = window.innerHeight || document.documentElement.clientHeight
  const vw   = window.innerWidth  || document.documentElement.clientWidth
  if (rect.height <= 0 || rect.width <= 0 || vh <= 0 || vw <= 0) return false

  const visibleH = Math.max(0, Math.min(rect.bottom, vh) - Math.max(rect.top,  0))
  const visibleW = Math.max(0, Math.min(rect.right,  vw) - Math.max(rect.left, 0))
  if (visibleH <= 0 || visibleW <= 0) return false

  const visibleArea = visibleH * visibleW

  if (visibleArea / (rect.height * rect.width) >= VIEWABLE_FRACTION) return true

  return visibleArea / (vh * vw) >= VIEWABLE_FRACTION
}

/**
 * Read a source that may be a ref, a computed, or a plain getter function.
 *
 * `unref` returns non-refs untouched, so a getter handed straight to it comes
 * back as the function object rather than the URL. Every truthiness guard then
 * passes and `fetch` stringifies the function source into a same-origin URL —
 * `/short-selling-sounds-like-solution/()=>t.value?.impressionUrl??null` — which
 * 404s against the content site, so the ad server never sees the impression.
 *
 * @template T
 * @param {import('vue').Ref<T>|(() => T)|T} source
 * @returns {T}
 */
function resolve(source) {
  return typeof source === 'function' ? source() : unref(source)
}

/**
 * Fires a single AIZEn impression pixel once the element has been viewable for
 * one continuous second.
 *
 * @param {import('vue').Ref<Element|null>} elementRef        Template ref wrapping the creative
 * @param {import('vue').Ref<string|null>|(() => string|null)} impressionUrlRef  Resolved AIZEn impression URL
 * @param {{ firedRef?: import('vue').Ref<boolean> }} [options]
 *   Pass `firedRef` (typically a `useState`) when one slot renders in more than
 *   one place — header plus mobile menu, say — so the two placements share a
 *   single impression instead of counting the same serve twice.
 * @returns {{ hasFired: import('vue').Ref<boolean> }}
 */
export function useAizenImpression(elementRef, impressionUrlRef, options = {}) {
  const hasFired = options.firedRef ?? ref(false)

  if (!process.client) return { hasFired }

  let observer     = null
  let sampler      = null
  let visibleSince = null

  const cleanup = () => {
    if (observer) {
      observer.disconnect()
      observer = null
    }
    if (sampler) {
      clearInterval(sampler)
      sampler = null
    }
    visibleSince = null
  }

  // Only a non-empty string is ever handed to `fetch`, so a mis-typed source can
  // never be coerced into a bogus same-origin request.
  const impressionUrl = () => {
    const url = resolve(impressionUrlRef)
    return typeof url === 'string' && url ? url : null
  }

  const fire = () => {
    const url = impressionUrl()
    if (hasFired.value || !url) return

    hasFired.value = true
    cleanup()

    try {
      fetch(url, { keepalive: true }).catch(() => {})
    } catch {
      // impression tracking must never break the page
    }
  }

  // Dwell is sampled rather than driven by threshold crossings: a creature
  // taller than the viewport can scroll for thousands of pixels without ever
  // crossing a fixed threshold, which would leave a crossing-based observer
  // silent for the whole page view.
  const sample = () => {
    if (hasFired.value) return

    const el = resolve(elementRef)
    if (!el || document.hidden || !isAdViewable(el)) {
      visibleSince = null
      return
    }

    if (visibleSince === null) {
      visibleSince = performance.now()
      return
    }

    if (performance.now() - visibleSince >= DWELL_MS) fire()
  }

  const startSampling = () => {
    if (sampler || hasFired.value) return
    sample()
    sampler = window.setInterval(sample, SAMPLE_MS)
  }

  const stopSampling = () => {
    if (sampler) {
      clearInterval(sampler)
      sampler = null
    }
    visibleSince = null
  }

  const attach = () => {
    if (hasFired.value) return

    const el  = resolve(elementRef)
    const url = impressionUrl()
    // Not a permanent bail: the watcher below re-runs attach() as soon as the
    // element ref or the served URL lands.
    if (!el || !url) return

    if (typeof IntersectionObserver === 'undefined') {
      startSampling()
      return
    }

    if (observer) observer.disconnect()
    observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) startSampling()
        else stopSampling()
      },
      { threshold: 0 },
    )
    observer.observe(el)
  }

  watch(
    [() => resolve(elementRef), () => impressionUrl()],
    attach,
    { flush: 'post', immediate: true },
  )

  onBeforeUnmount(cleanup)

  return { hasFired }
}
